import { Injectable } from '@angular/core';
import {
  AddAddressContent,
  LoginFormValidate
} from '@com-delta-idp/mnx-login-ui';
import { AnalyticsService } from '@com-delta-idp/mnx-utils';
import { BehaviorSubject } from 'rxjs';
import { finalize, map, switchMap, takeUntil } from 'rxjs/operators';
import { AddAddressAdapterService } from '../../adapters/add-address/add-address-adapter.service';
import { ANALYTICS_EVENTS_LOGIN } from '../../constants/analytics-constants';
import {
  ADD_ADDRESS_DEFAULT_COUNTRY_CODE,
  STANDALONE_PAGE_URL,
  VIEW_NAME
} from '../../constants/login-constant';
import { LoginModalContent } from '../../models/aem';
import { IS_STANDALONE_PAGE$ } from '../facade.service';
import { AddAddressHelperFacadeService } from './add-address-helper-facade.service';

@Injectable({
  providedIn: 'root'
})
export class AddAddressFacadeService extends AddAddressHelperFacadeService {
  aemContent!: LoginModalContent;
  addAddressContent$ = new BehaviorSubject<AddAddressContent | null>(null);

  constructor(
    public addAddressAdapter: AddAddressAdapterService,
    private analytics: AnalyticsService
  ) {
    super();
  }

  getAddAddressPageContent(viewName: string): void {
    this.addAddressApiService
      .getCountryCodeAndContent()
      .pipe(
        switchMap(({ countryCodes, aemResponse }) => {
          const defaultCountry =
            aemResponse.addAddress?.defaultValueCountry ||
            ADD_ADDRESS_DEFAULT_COUNTRY_CODE;
          aemResponse.addAddress.defaultValueCountry = defaultCountry;
          return this.addAddressApiService.getAddressLines(defaultCountry).pipe(
            map((addressLines) => ({
              aemResponse,
              countryCodes,
              addressLines
            }))
          );
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(
        (response) => {
          this.aemContent = response.aemResponse;
          if (this.checkApiAndAemResponse(response)) {
            this.addAddressError$.next(this.handleDefaultError());
          } else {
            const content =
              this.addAddressAdapter.getAddAddressContent(response);
            this.addAddressContent$.next(content);
            if (IS_STANDALONE_PAGE$.getValue() === false) {
              this.nextDisplayViewService.nextDisplayView(viewName);
            } else {
              this.router.navigateByUrl(`${STANDALONE_PAGE_URL[viewName]}`);
            }
          }
        },
        (error) => this.handleError(error.error?.errors)
      );
  }

  onFormSubmit(): boolean | void {
    if (this.validateForm()) {
      return false;
    }
    const requestObject = this.addAddressAdapter.buildRequestObject(
      this.getAddAddressContent()
    );
    this.addAddressApiService
      .validateAddress(requestObject)
      .pipe(
        takeUntil(this.unsubscribe$),
        finalize(() => this.disableActionButton(false))
      )
      .subscribe(
        (response) => {
          if (
            !response?.isSuccess ||
            (response?.errors && response?.errors.length > 0)
          ) {
            this.handleError(response?.errors);
          } else {
            this.handleSuccess();
          }
        },
        (error) => this.handleError(error?.error?.errors)
      );
  }

  handleSuccess(): void {
    this.resetForm();
    const setPasswordScreens = VIEW_NAME.SET_PASSWORD_SCREEN;
    this.analytics.dispatchCustomEvent(
      ANALYTICS_EVENTS_LOGIN.SET_PASSWORD_LOADED,
      true
    );
    this.nextDisplayViewService.nextDisplayView(setPasswordScreens);
  }

  formValueChange(): void {
    const content: AddAddressContent = this.getAddAddressContent();
    if (
      content.formAttributes.submitAttempt &&
      content.formAttributes.errorAlertBanner &&
      content.formAttributes.errorCount
    ) {
      this.getAddAddressContent().formGroup.valueChanges.subscribe(() => {
        this.getErrorCount();
      });
    }
  }

  validateForm() {
    LoginFormValidate.touchControlsAddAddressOnSubmit(
      this.getAddAddressContent()
    );
    this.getAddAddressContent().formAttributes.submitAttempt = true;
    return this.getErrorCount();
  }

  getErrorCount(): boolean {
    const errorCount = LoginFormValidate.validateFormFields(
      this.getAddAddressContent().formGroup
    );
    this.getAddAddressContent().formAttributes.errorCount = errorCount;
    this.getAddAddressContent().formAttributes.errorAlertBanner =
      LoginFormValidate.getErrorAlert(this.getAddAddressContent().formGroup);
    return errorCount > 0;
  }

  addAddressFormFocusChange(fieldName: string): void {
    const content: AddAddressContent = this.getAddAddressContent();
    LoginFormValidate.touchControlAddAddress(fieldName, content);
  }
}
